"use client";

import { useEffect, useRef } from "react";

type Props = {
  className?: string;
  /** 粒子数の倍率（1=約1100粒） */
  density?: number;
  /** 暗い背景に重ねる場合 true（加算合成で発光的に） */
  dark?: boolean;
};

/**
 * Three.js の粒子背景。
 * - ロゴの teal/水色に合わせた淡いシアン系パレット
 * - ゆるやかな波打ち + 回転 + マウスパララックス
 * - prefers-reduced-motion 尊重 / 画面外で一時停止 / WebGL不可時は何も描かない（背景グラデのみ）
 * - three は動的 import（メインバンドルに載せない・クライアントのみ）
 */
export default function ParticleBackground({
  className,
  density = 1,
  dark = false,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let disposed = false;
    let cleanup = () => {};
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    (async () => {
      let THREE: typeof import("three");
      try {
        THREE = await import("three");
      } catch {
        return;
      }
      if (disposed || !ref.current) return;

      const host = ref.current;
      let width = host.clientWidth || 1;
      let height = host.clientHeight || 1;

      let renderer: import("three").WebGLRenderer;
      try {
        renderer = new THREE.WebGLRenderer({
          antialias: true,
          alpha: true,
          powerPreference: "low-power",
        });
      } catch {
        return; // WebGL 不可 → グラデ背景だけ残す
      }
      renderer.setSize(width, height);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      renderer.domElement.style.display = "block";
      host.appendChild(renderer.domElement);

      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(60, width / height, 1, 3000);
      camera.position.z = 560;

      // 柔らかい円形スプライト
      const cv = document.createElement("canvas");
      cv.width = cv.height = 64;
      const ctx = cv.getContext("2d")!;
      const grad = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
      grad.addColorStop(0, "rgba(255,255,255,1)");
      grad.addColorStop(0.35, "rgba(255,255,255,0.75)");
      grad.addColorStop(1, "rgba(255,255,255,0)");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, 64, 64);
      const sprite = new THREE.CanvasTexture(cv);

      // teal / 水色パレット（ロゴ準拠）
      const palette = [
        new THREE.Color("#3faeb3"),
        new THREE.Color("#5bc5c0"),
        new THREE.Color("#7fd4d0"),
        new THREE.Color("#a8e0dd"),
        new THREE.Color("#cdeef0"),
      ];

      const COUNT = Math.floor(1100 * Math.max(0.3, density));
      const RANGE_X = 1500;
      const RANGE_Y = 460;
      const RANGE_Z = 1000;
      const positions = new Float32Array(COUNT * 3);
      const base = new Float32Array(COUNT * 3);
      const colors = new Float32Array(COUNT * 3);
      const sizes = new Float32Array(COUNT);

      for (let i = 0; i < COUNT; i++) {
        const x = (Math.random() - 0.5) * RANGE_X;
        const y = (Math.random() - 0.5) * RANGE_Y;
        const z = (Math.random() - 0.5) * RANGE_Z;
        positions[i * 3] = base[i * 3] = x;
        positions[i * 3 + 1] = base[i * 3 + 1] = y;
        positions[i * 3 + 2] = base[i * 3 + 2] = z;
        const col = palette[(Math.random() * palette.length) | 0];
        colors[i * 3] = col.r;
        colors[i * 3 + 1] = col.g;
        colors[i * 3 + 2] = col.b;
        sizes[i] = 2 + Math.random() * 5;
      }

      const geo = new THREE.BufferGeometry();
      geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
      geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));

      const mat = new THREE.PointsMaterial({
        size: dark ? 7 : 6,
        map: sprite,
        vertexColors: true,
        transparent: true,
        opacity: dark ? 0.9 : 0.62,
        depthWrite: false,
        sizeAttenuation: true,
        blending: dark ? THREE.AdditiveBlending : THREE.NormalBlending,
      });

      const points = new THREE.Points(geo, mat);
      scene.add(points);

      let mouseX = 0;
      let mouseY = 0;
      const onMove = (e: PointerEvent) => {
        mouseX = e.clientX / window.innerWidth - 0.5;
        mouseY = e.clientY / window.innerHeight - 0.5;
      };
      window.addEventListener("pointermove", onMove, { passive: true });

      const clock = new THREE.Clock();
      let raf = 0;
      const posAttr = geo.attributes.position as import("three").BufferAttribute;

      const frame = (animate: boolean) => {
        const t = clock.getElapsedTime();
        const arr = posAttr.array as Float32Array;
        for (let i = 0; i < COUNT; i++) {
          const ix = i * 3;
          arr[ix + 1] =
            base[ix + 1] +
            Math.sin(t * 0.5 + base[ix] * 0.004 + base[ix + 2] * 0.004) * 16;
        }
        posAttr.needsUpdate = true;
        points.rotation.y = t * 0.025;

        camera.position.x += (mouseX * 140 - camera.position.x) * 0.04;
        camera.position.y += (-mouseY * 90 - camera.position.y) * 0.04;
        camera.lookAt(scene.position);

        renderer.render(scene, camera);
        if (animate) raf = requestAnimationFrame(() => frame(true));
      };

      const start = () => {
        if (raf || reduce) return;
        raf = requestAnimationFrame(() => frame(true));
      };
      const stop = () => {
        if (raf) cancelAnimationFrame(raf);
        raf = 0;
      };

      // 初回描画（reduce時は静止 1 フレーム）
      frame(!reduce);

      const ro = new ResizeObserver(() => {
        width = host.clientWidth || 1;
        height = host.clientHeight || 1;
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
        renderer.setSize(width, height);
      });
      ro.observe(host);

      const io = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) start();
          else stop();
        },
        { threshold: 0 }
      );
      io.observe(host);

      cleanup = () => {
        stop();
        window.removeEventListener("pointermove", onMove);
        ro.disconnect();
        io.disconnect();
        geo.dispose();
        mat.dispose();
        sprite.dispose();
        renderer.dispose();
        renderer.domElement.remove();
      };
    })();

    return () => {
      disposed = true;
      cleanup();
    };
  }, [density, dark]);

  return <div ref={ref} className={className} aria-hidden />;
}
